/**
 * Region (multi-deployment) config.
 *
 * EU and US are independent production deployments on separate hosts, each with
 * its own session/auth. "Switching" region therefore opens the other host's
 * root in a new browser tab — not a client-side route — and the user may need
 * to sign in again on the destination.
 */

export type RegionId = "eu" | "us";

export interface Region {
  id: RegionId;
  label: string;
  /** Production hostnames that resolve to this region. */
  hostnames: string[];
  /** Root URL to navigate to when switching into this region. */
  url: string;
}

export const REGIONS: Region[] = [
  {
    id: "eu",
    label: "EU",
    // Apex is the EU deployment; eu.* alias is also accepted.
    hostnames: ["platform.composo.ai", "eu.platform.composo.ai"],
    url: "https://platform.composo.ai",
  },
  {
    id: "us",
    label: "US",
    hostnames: ["us.platform.composo.ai"],
    url: "https://us.platform.composo.ai",
  },
];

/**
 * Best-effort detection of the region currently serving the app, from the
 * browser host. Returns null for unknown hosts (localhost, preview builds) so
 * callers don't render a misleading "current region" indicator.
 */
export function getCurrentRegion(
  hostname: string = window.location.hostname,
): Region | null {
  return REGIONS.find((region) => region.hostnames.includes(hostname)) ?? null;
}
